import React, { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import Downshift from 'downshift';
import AutoSizer from 'react-virtualized/dist/commonjs/AutoSizer';
import List from 'react-virtualized/dist/commonjs/List';

import { AssociatedKeyRelatedToAccount } from '@block-explorer/entities';
import {
  FlexColumn,
  FlexRow,
  Input,
  SvgIcon,
  useMatchMedia,
  useGetInputInteractionType,
  InputInteractionType,
  ArrowDownIcon,
  CrossIcon,
} from '@make-software/cspr-design';
import { BodyText } from '@block-explorer/ui';
import { isValidPublicKey } from '../../utils/account';
import { AssociatedAccountRowWithCsprName } from './associated-account-row';
import Skeleton from 'react-loading-skeleton';
import { useClickAndTouchAway } from '../validator-dropdown/hooks/useClickAndTouchAway';
import isEqual from 'fast-deep-equal';

const ComponentContainer = styled.div<{ disabled?: boolean }>(
  ({ theme, disabled }) => ({
    outline: 'none',
    ...(disabled && {
      opacity: 0.5,
    }),
  })
);

const InputContainer = styled.div(({ theme }) => ({
  minHeight: 36,
  maxHeight: 88,
  width: '100%',
}));

const ClearSvgIcon = styled(SvgIcon)(({ theme }) => ({
  path: {
    fill: theme.styleguideColors.contentTertiary,
  },
}));

const ArrowSvgIcon = styled(SvgIcon)(({ theme }) => ({
  path: {
    fill: theme.styleguideColors.contentPrimary,
  },
  marginLeft: 16,
}));

const InputIconRow = styled(FlexRow)(({ theme }) => ({
  width: '48px',
}));

const MenuEmptyContainer = styled.div(({ theme }) => ({
  padding: '8px 16px',
  pointerEvents: 'none',
}));

const MenuContainer = styled.div<{ isOpen: boolean }>(({ theme, isOpen }) => ({
  display: isOpen ? 'block' : 'none',
  marginTop: 4,
  borderRadius: theme.borderRadius.base,
  maxHeight: 264,
}));

const StyledDropdownRow = styled(FlexRow)(({ theme, isHighlighted }) => ({
  backgroundColor: theme.styleguideColors.fillSecondary,
  padding: '0 15px',
  cursor: 'pointer',
  ...(isHighlighted && {
    '&:not(:hover)': {
      border: `1px solid ${theme.styleguideColors.contentBlue}`,
      borderRadius: theme.borderRadius.base,
    },
  }),
}));

const SelectedItemContainer = styled(FlexRow)(({ theme }) =>
  theme.withMedia({
    width: '100%',
    borderRadius: theme.borderRadius.base,
    backgroundColor: theme.styleguideColors.fillSecondary,
    padding: '0 15px',
  })
);

const ITEM_HEIGHT = 48;
const MOBILE_ITEM_HEIGHT = 66;
const MAX_VISIBLE_ITEMS_AMOUNT = 4;

enum InputType {
  selectedItem = 'selectedItem',
  input = 'input',
}

const SelectedAccountRow = ({ selectedItem, handleClickSelectedItem }) => {
  return (
    <SelectedItemContainer
      align={'center'}
      justify={'space-between'}
      onClick={handleClickSelectedItem}
      onKeyDown={(ev) => {
        if (ev.key === 'Enter') {
          handleClickSelectedItem();
        }
      }}
    >
      <AssociatedAccountRowWithCsprName data={selectedItem} isTitle />
      <ArrowSvgIcon
        src={ArrowDownIcon}
        alt={'Arrow down icon'}
        role={'img'}
        tabIndex={0}
        onClick={handleClickSelectedItem}
        rotate={false}
        marginLeft
      />
    </SelectedItemContainer>
  );
};

interface AssosiatedAccountsDropdownProps {
  data: AssociatedKeyRelatedToAccount[] | null;
  loading: boolean;
  error: string | null;
  onSelect: (item: AssociatedKeyRelatedToAccount | null) => void;
  disabled?: boolean;
  value?: AssociatedKeyRelatedToAccount | null;
  isTransfer?: boolean;
}

export const AssociatedAccountsDropdown = ({
  id = 'associated-keys-dropdown',
  data,
  loading,
  error,
  disabled,
  onSelect,
  value,
  isTransfer,
}: AssosiatedAccountsDropdownProps) => {
  const { t } = useTranslation();
  const inputRef = useRef<HTMLInputElement>();

  const [searchPublicKey, setSearchPublicKey] = useState<string>('');
  const [associatedKeys, setAssociatedKeys] = useState<
    AssociatedKeyRelatedToAccount[] | null
  >(null);
  const [inputType, setInputType] = useState<InputType>(
    value ? InputType.selectedItem : InputType.input
  );

  useEffect(() => {
    if (data) {
      setAssociatedKeys(data);
    }
  }, [data]);

  const { ref: outerClickRef } = useClickAndTouchAway({
    callback: () => {
      if (value && inputType === InputType.input) {
        setInputType(InputType.selectedItem);
      }
    },
  });

  const handleClearSearch = () => {
    setSearchPublicKey('');
    onSelect(null);
  };

  const handleChangeInput = (text) => {
    setSearchPublicKey(text);
  };

  const handleClickSelectedItem = () => {
    setInputType(InputType.input);
  };

  const handleItemSelect = (item) => {
    setInputType(InputType.selectedItem);
    onSelect(item);
  };

  const getItemToString = (item) => {
    if (item === null) return '';
    return typeof item === 'string' ? item : item.public_key;
  };

  const rowHeight = useMatchMedia(
    [MOBILE_ITEM_HEIGHT, ITEM_HEIGHT, ITEM_HEIGHT],
    []
  );

  const placeholder = t('Enter account public key', 'Enter account public key');
  const stateReducer = (state, changes) => {
    switch (changes.type) {
      case Downshift.stateChangeTypes.controlledPropUpdatedSelectedItem: {
        // close menu on item selection
        return {
          ...changes,
          isOpen: false,
        };
      }
      default: {
        return changes;
      }
    }
  };

  const dropdownInputName = isTransfer ? t('Sender') : t('Account');
  const inputInteractionTypeRef = useGetInputInteractionType();

  return (
    <Downshift
      itemToString={getItemToString}
      onSelect={handleItemSelect}
      selectedItem={value}
      stateReducer={stateReducer}
      selectedItemChanged={(prevItem, item) => !isEqual(prevItem, item)}
      onInputValueChange={handleChangeInput}
    >
      {(downshift) => {
        const {
          getItemProps,
          getMenuProps,
          isOpen,
          getRootProps,
          getInputProps,
          selectedItem,
          highlightedIndex,
        } = downshift;
        const visibleItems =
          searchPublicKey && associatedKeys
            ? (associatedKeys || []).filter((item) =>
                item.public_key.includes(searchPublicKey)
              )
            : associatedKeys || [];

        const noItems = !(visibleItems && visibleItems.length);

        const handleFocus = () => {
          if (disabled) {
            return;
          }
          !isOpen && downshift.openMenu();
        };

        return (
          <ComponentContainer
            {...getRootProps({ refKey: 'innerRef' })}
            tabIndex={0}
            disabled={disabled}
            ref={outerClickRef}
          >
            <FlexColumn gap={4}>
              <BodyText
                size={1}
                scale={'sm'}
                aria-label={dropdownInputName}
                id={id}
                tabIndex={0}
              >
                {dropdownInputName}
              </BodyText>
              <InputContainer onClick={handleFocus}>
                {inputType === InputType.input ? (
                  <Input
                    id={'associated-accounts-input'}
                    suffixIcon={
                      <InputIconRow justify={'end'}>
                        {searchPublicKey && (
                          <ClearSvgIcon
                            src={CrossIcon}
                            onClick={handleClearSearch}
                            alt={'Cross icon to clear'}
                            onKeyDown={(ev) => {
                              if (ev.key === 'Enter') {
                                handleClearSearch();
                              }
                            }}
                            role={'img'}
                            tabIndex={0}
                          />
                        )}
                        <ArrowSvgIcon
                          src={ArrowDownIcon}
                          alt={'Arrow down icon'}
                          rotate={isOpen}
                          onKeyDown={(ev) => {
                            if (ev.key === 'Enter') {
                              handleFocus(ev);
                            }
                          }}
                          aria-haspopup="listbox"
                          aria-expanded={isOpen}
                          tabIndex={0}
                        />
                      </InputIconRow>
                    }
                    {...(getInputProps({
                      innerRef: inputRef,
                      placeholder,
                      value: searchPublicKey || '',
                      disabled,
                    }) as any)}
                  />
                ) : (
                  <SelectedAccountRow
                    selectedItem={selectedItem}
                    handleClickSelectedItem={handleClickSelectedItem}
                  />
                )}
              </InputContainer>
            </FlexColumn>
            <MenuContainer {...getMenuProps()} isOpen={isOpen} tabIndex={0}>
              {isOpen &&
                (loading ? (
                  <MenuEmptyContainer>
                    <Skeleton height={40} />
                  </MenuEmptyContainer>
                ) : isValidPublicKey(searchPublicKey) && error ? (
                  <MenuEmptyContainer>
                    <BodyText size={3} variation="darkGray">
                      No associated keys related to this account found
                    </BodyText>
                  </MenuEmptyContainer>
                ) : noItems ? (
                  <MenuEmptyContainer>
                    <BodyText size={3} variation="darkGray">
                      No associated keys found
                    </BodyText>
                  </MenuEmptyContainer>
                ) : (
                  <>
                    <AutoSizer disableHeight>
                      {({ width }) => (
                        <List
                          overscanRowCount={4}
                          height={Math.min(
                            MAX_VISIBLE_ITEMS_AMOUNT * rowHeight,
                            visibleItems.length * rowHeight
                          )}
                          rowCount={visibleItems.length}
                          rowHeight={rowHeight}
                          rowRenderer={({ index, style }) => {
                            const item = visibleItems[index];
                            const isHighlighted =
                              inputInteractionTypeRef.current ===
                                InputInteractionType.keyboard &&
                              highlightedIndex !== null &&
                              highlightedIndex === index;

                            return (
                              <StyledDropdownRow
                                key={item.account_hash}
                                style={style}
                                {...getItemProps({ item, index })}
                                isHighlighted={isHighlighted}
                              >
                                <AssociatedAccountRowWithCsprName
                                  data={item}
                                  loading={loading}
                                />
                              </StyledDropdownRow>
                            );
                          }}
                          width={width}
                        />
                      )}
                    </AutoSizer>
                  </>
                ))}
            </MenuContainer>
          </ComponentContainer>
        );
      }}
    </Downshift>
  );
};
